import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { StorageTemporaryLinkPipe } from '@interacta-shared/data-access-storage-temporary-link';
import {
    ApplyPipe,
    BadgeComponent,
    IconButtonComponent,
    ImageComponent,
    MenuComponent,
    MenuItemDecoratorDirective,
} from '@interacta-shared/ui';
import { IconComponent } from '@interacta-shared/ui-icons';
import { getRandomSecondaryColorVariables, uuid } from '@interacta-shared/util';
import { AttachmentIconComponent } from '@interacta/ui-attachment';
import { getAttachmentPreviewImageLink } from '@interacta/util-post';
import { AI_SHARED_SMART_ACTIONS } from '@modules/ai-shared/constants/smart-actions.constants';
import {
    getReferencePreviewText,
    getReferenceTitle,
    getReferenceUnavailableLabel,
} from '@modules/ai-shared/helpers/ai-shared.utils';
import {
    AISharedGenericReference,
    AISharedReferenceType,
    AISharedSmartAction,
} from '@modules/ai-shared/models/ai-shared.model';
import { AISharedReferenceViewService } from '@modules/ai-shared/services/ai-shared-reference-view.service';
import { getCustomPostPreviewImage } from '@modules/post/utils/post.utils';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'interacta-ai-shared-references',
    templateUrl: './ai-shared-references.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        NgStyle,
        AttachmentIconComponent,
        BadgeComponent,
        CdkOverlayOrigin,
        IconButtonComponent,
        IconComponent,
        ImageComponent,
        MenuComponent,
        MenuItemDecoratorDirective,
        ApplyPipe,
        TranslatePipe,
        StorageTemporaryLinkPipe,
        AsyncPipe,
    ],
})
export class AISharedReferencesComponent {
    private readonly referenceViewService = inject(
        AISharedReferenceViewService,
    );
    private readonly translateService = inject(TranslateService);

    references = input.required<Array<AISharedGenericReference>>();
    /** Where the list is rendered from, for analytics: the sources section under
     * the answer ('list') or the popover of an in-text reference chip ('inline'). */
    readonly origin = input<'inline' | 'list'>('list');
    readonly smartActionsDisabled = input(false);
    /**
     * Attachments open an in-app overlay instead of navigating, so a host that
     * shows this list inside a popover has to dismiss it on its own.
     */
    readonly referenceOpened = output<void>();
    readonly askReference = output<AISharedGenericReference>();
    readonly smartAction = output<{
        reference: AISharedGenericReference;
        action: AISharedSmartAction;
    }>();

    /** Index of the source whose smart-action menu is open. */
    readonly openedSmartActions = signal<number | null>(null);
    readonly smartActionsMenuId = `ai-reference-smart-actions-${uuid()}`;

    readonly smartActions = AI_SHARED_SMART_ACTIONS;
    readonly getRandomSecondaryColorVariables =
        getRandomSecondaryColorVariables;
    readonly getCustomPostPreviewImage = getCustomPostPreviewImage;
    readonly getAttachmentPreviewImageLink = getAttachmentPreviewImageLink;
    readonly getReferenceUnavailableLabel = getReferenceUnavailableLabel;
    readonly getReferenceTitle = (
        reference: AISharedGenericReference,
    ): string =>
        getReferenceTitle(
            reference,
            (key, params) =>
                this.translateService.instant(key, params) as string,
        );
    readonly getReferencePreviewText = (
        reference: AISharedGenericReference,
    ): string =>
        getReferencePreviewText(
            reference,
            (key, params) =>
                this.translateService.instant(key, params) as string,
        );
    readonly AISharedReferenceType = AISharedReferenceType;

    emitSmartAction(
        reference: AISharedGenericReference,
        action: AISharedSmartAction,
    ): void {
        this.smartAction.emit({ reference, action });
    }

    openRefPreview(ref: AISharedGenericReference): void {
        this.referenceViewService.open(ref, this.origin());
        this.referenceOpened.emit();
    }
}
